import React from 'react';
import { useSelector } from 'react-redux';
import { ArrayHelpers, Copy, StringHelpers, SystemText } from 'e4shared';
import CompetencyTasks from 'views/user/components/competencyTasks';
import CompetencyOverTime from 'views/user/1-2-1s/components/competencyOverTime';
import Intro from './components/intro';
import Summary from './components/summary';
import Rating, { CalcAutoValue, CalculatePIRating } from './components/rating';
import { hasModule } from 'helpers/profile';
import { CompetencyGridUsers } from 'views/myDashboard/components/widgets/competencyGrid/components/users';
import { e4Format, isPast, isToday } from 'helpers/dates';
import { withRouter } from 'react-router-dom';
import { translateCompetency, translatePI } from 'helpers/translations';

export const Wizard = ({
    appraisal,
    profile,
    state,
    setStage,
    setAgreedRatingValue,
    inReview,
    setInReview,
    updateAppraisal,
    unassignReschedule,
    showBothManagerAndStaffRatingInfo,
    congrats,
    setCongrats,
    permissions,
    finalise,
    inPreview,
    history,
    match,
}) => {
    const [error, setError] = React.useState('');
    const [pages, setPages] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [startPage, setStartPage] = React.useState(0);
    const [endPage, setEndPage] = React.useState(0);
    const [competency, setCompetency] = React.useState(null);
    const [comments, setComments] = React.useState('');
    const [finalComments, setFinalComments] = React.useState('');
    const [goals, setGoals] = React.useState('');
    const [predictions, setPredictions] = React.useState({});
    const [sliding, setSliding] = React.useState(false);
    const [showTasks, setShowTasks] = React.useState(false);
    const [showOverTime, setShowOverTime] = React.useState(false);
    const [showRestOfTeam, setShowRestOfTeam] = React.useState(false);

    const { user } = useSelector(state => state.user);
    const guid = match.params.guid;
    const usePIs = user.companySettings?.development?.usePositiveIndicators;

    const ratingRange = user.companySettings?.display?.ratingRange || 5;

    React.useEffect(() => {
        const jsonGoals = state.user
            ? appraisal?.user_Goals || appraisal?.manager_Goals
            : appraisal?.manager_Goals || appraisal?.user_Goals;
        setGoals(JSON.parse(jsonGoals || '[]'));
    }, [appraisal?.id, state.user]);

    React.useEffect(() => {
        if (state.finalStarted) {
            setFinalComments(comments);
        }
    }, [comments]);

    React.useEffect(() => {
        if (profile.id && appraisal?.competencies) {
            setPages(
                appraisal?.competencies
                    .map(c => translateCompetency(user.language_Code, c))
                    .reduce((a, c) => {
                        let temp = [];
                        if (c.positiveIndicators?.length) {
                            temp = c.positiveIndicators
                                .map(pi => translatePI(user.language_Code, pi))
                                .map((pi, i) => ({
                                    ...pi,
                                    ...c,
                                    positiveIndicator: pi,
                                    piIndex: i + 1,
                                }));

                            if (c.review_Comment) {
                                temp = temp.concat([{ ...c, comment: true }]);
                            }
                        } else {
                            temp = [{ ...c }];
                        }

                        return a.concat(temp);
                    }, [])
            );

            if (state.inProgress) {
                setComments(appraisal.comments || finalComments);
            } else {
                setComments(state.user ? appraisal.user_Comments : appraisal.manager_Comments);
            }

            const userAppPredictions = JSON.parse(
                appraisal.user_Predictions || appraisal.finalAppraisal?.user_Predictions || '[]'
            );

            const managerAppPredictions = JSON.parse(
                appraisal.manager_Predictions || appraisal.finalAppraisal?.manager_Predictions || '[]'
            );

            const appPredictions = state.user ? userAppPredictions : managerAppPredictions;

            const commentsParsed = comments => {
                if (comments === '') comments = null;
                return JSON.parse(comments || '[]');
            };

            const appComments = commentsParsed(
                state.user ? appraisal.user_Competency_Comments : appraisal.manager_Competency_Comments
            );

            const temp = {};

            const managerCommentsParsed = commentsParsed(appraisal.manager_Competency_Comments);
            const userCommentsParsed = commentsParsed(appraisal.user_Competency_Comments);

            appraisal.competencies.forEach(c => {
                temp[c.id] = {};
                temp[c.id].level = appPredictions.find(p => p.competency_Id === c.id)?.level;
                temp[c.id].comments = appComments.find(p => p.competency_Id === c.id)?.comments;

                if (state.inProgress) {
                    temp[c.id].userLevel = userAppPredictions.find(p => p.competency_Id === c.id)?.level;
                    temp[c.id].managerLevel = managerAppPredictions.find(p => p.competency_Id === c.id)?.level;
                    temp[c.id].userComments = userCommentsParsed.find(p => p.competency_Id === c.id)?.comments;
                    temp[c.id].managerComments = managerCommentsParsed.find(p => p.competency_Id === c.id)?.comments;
                }

                temp[c.id].positiveIndicators = appPredictions.find(p => p.competency_Id === c.id)?.positiveIndicators || {};

                temp[c.id].managerPositiveIndicators =
                    managerAppPredictions.find(p => p.competency_Id === c.id)?.positiveIndicators || {};

                temp[c.id].userPositiveIndicators =
                    userAppPredictions.find(p => p.competency_Id === c.id)?.positiveIndicators || {};

                //TODO - SORT THIS!
                // if (goals && ArrayHelpers.existsAndHasLength(c.positiveIndicators)) {
                //     c.positiveIndicators.forEach((pi, i) => {
                //         const defaultValue = CalcAutoValue(user, goals, pi);
                //         temp[c.id].managerPositiveIndicators[i + 1] =
                //             temp[c.id].managerPositiveIndicators[i + 1] ?? defaultValue;
                //         temp[c.id].userPositiveIndicators[i + 1] = temp[c.id].userPositiveIndicators[i + 1] ?? defaultValue;
                //     });

                //     temp[c.id].level = inPreview ? 0 : CalculatePIRating(c, predictions);
                // }
            });

            setPredictions(temp);
        }
    }, [appraisal?.id, profile.id, state.user, goals, state]);

    React.useEffect(() => {
        if (startPage && page >= 1) {
            setCompetency(pages[startPage - 1 + page - 1]);
        } else {
            setCompetency(pages[page - 1]);
        }
    }, [startPage, pages, page]);

    React.useEffect(() => {
        if (appraisal?.submission || state.completed) setPage(pages.length + 1);
    }, [pages, state.completed, appraisal?.submission]);

    React.useEffect(() => {
        if (error) {
            const container = document.getElementById('assessment-wizard');
            const error = document.getElementById('error-anchor');
            let topPos = error.offsetTop;
            container.scrollTop = topPos;
        }
    }, [error]);

    if (!appraisal || !profile) return null;

    const save = completed => {
        const cleanPredictions = pages.reduce((a, c) => {
            const existing = a.find(x => x.competency_Id === c.id);
            if (existing) {
                return a;
            } else {
                return a.concat([
                    {
                        competency_Id: c.id,
                        level: predictions[c.id]?.level,
                        positiveIndicators: predictions[c.id]?.positiveIndicators || {},
                    },
                ]);
            }
        }, []);

        const cleanComments = pages.reduce((a, c) => {
            const existing = a.find(x => x.competency_Id === c.id);
            if (existing) {
                return a;
            } else {
                return a.concat([
                    {
                        competency_Id: c.id,
                        comments: predictions[c.id]?.comments,
                    },
                ]);
            }
        }, []);

        const updatedAppraisal = state.user
            ? {
                  ...appraisal,
                  user_Id: user.id,
                  user_Predictions: JSON.stringify(cleanPredictions),
                  user_Competency_Comments: JSON.stringify(cleanComments),
                  user_Comments: comments,
                  user_Comments_Done: completed ? new Date() : null,
              }
            : {
                  ...appraisal,
                  user_Id: user.id,
                  manager_Predictions: JSON.stringify(cleanPredictions),
                  manager_Competency_Comments: JSON.stringify(cleanComments),
                  manager_Comments: comments,
                  manager_Comments_Done: completed ? new Date() : null,
              };

        updateAppraisal(updatedAppraisal, completed);
    };

    const rate = (rating, level, positiveIndicators) => {
        const temp = { ...predictions };
        if (temp[rating.id]) {
            temp[rating.id].level = level;
            temp[rating.id].positiveIndicators = positiveIndicators || {};
        } else {
            temp[rating.id] = { level, positiveIndicators, comment: '' };
        }

        setPredictions(temp);
    };

    const comment = value => {
        const temp = { ...predictions };
        if (temp[competency.id]) {
            temp[competency.id].comments = value;
        } else {
            temp[competency.id] = { rating: 0, comment: value };
        }

        setPredictions(temp);
    };

    const piMissing = () => {
        if (!usePIs) return false;
        let result = false;
        const set = predictions[competency.id]?.positiveIndicators;
        if (!set[competency.piIndex]) result = true;

        return result;
    };

    const prev = () => {
        setError('');

        if (page >= 1) {
            setPage(page - 1);
        }

        save(false);
    };

    const next = forceBackToSummary => {
        if (forceBackToSummary === true) {
            setPage(pages.length + 1);
        } else if (state.readonly) {
            if (startPage > 0) {
                if (endPage + 1 - startPage === page) {
                    setStartPage(0);
                    setPage(pages.length + 1);
                } else {
                    setPage(page + 1);
                }
            } else {
                setPage(pages.length + 1);
            }
        } else if (
            !inPreview &&
            competency &&
            (!usePIs || !ArrayHelpers.existsAndHasLength(competency.positiveIndicators)) &&
            !predictions[competency.id]?.level
        ) {
            setError(SystemText('Reviews/Appraisals/Wizard/Errors/NoRating', ratingRange));
        } else if (
            !inPreview &&
            usePIs &&
            competency &&
            !pages[page - 1].comment &&
            ArrayHelpers.existsAndHasLength(competency.positiveIndicators) &&
            piMissing()
        ) {
            setError(SystemText('Reviews/Appraisals/Wizard/Errors/NoPIRating', ratingRange));
        } else if (
            !inPreview &&
            competency &&
            pages[page - 1].comment &&
            pages[page - 1]?.review_Comment &&
            pages[page - 1]?.review_Comment_Mandatory &&
            !predictions[competency.id]?.comments
        ) {
            setError(SystemText('Reviews/Appraisals/Wizard/Errors/NoComment'));
        } else if (
            page > pages.length &&
            appraisal.participants === 'UM' &&
            user.reviewViaSitdown &&
            !inPreview &&
            !comments
        ) {
            setError(SystemText('Reviews/Appraisals/Wizard/Errors/Submit'));
        } else {
            setError('');
            let nextPage = page + 1;
            if (startPage && nextPage > endPage - startPage + 1) nextPage = pages.length + 1;
            const finalise = page > pages.length;
            setPage(nextPage);
            startPage === 0 && nextPage > pages.length && setInReview(false);
            startPage !== 0 && nextPage > endPage - startPage && setInReview(false);
            save(finalise);
        }
    };

    const buttons = () => {
        if (inPreview) return null;

        const hasCRM = competency && user.crmIntegration && competency.crm_Job_Ids && competency.crm_Job_Ids.length > 0;

        const previousTasks = competency ? competency.tasks.length : 0;
        const pastTasks = competency
            ? competency.tasks.filter(t => isPast(t.due) && !isToday(t.due) && !t.date_Completed).length
            : 0;

        let integrationsList = competency?.integrationFields
            ? competency.integrationFields.reduce(
                  (all, cur) => (all.some(x => x === cur.integration) ? all : all.concat([cur.integration])),
                  []
              )
            : [];
        integrationsList = hasCRM ? [user.crmIntegration].concat(integrationsList) : integrationsList;
        const withText =
            integrationsList.length === 0
                ? ''
                : SystemText(
                      'Reviews/Appraisals/Wizard/Images/IntegrationList/Title',
                      StringHelpers.arrayToString(integrationsList, ',', 'and')
                  );

        return (
            <div className="btn-row no-wrap">
                {state.manager && (
                    <button
                        onClick={() => setShowRestOfTeam(true)}
                        data-testid="button-show-team"
                        className="btn btn-xs btn-info hidden-sm hidden-xs"
                        title={SystemText('Reviews/Appraisals/Wizard/Buttons/ShowRestOfTeam/Title')}
                    >
                        <i className="fa fa-users"></i>
                    </button>
                )}
                <span className="btn-badge">
                    <button
                        onClick={() => setShowOverTime(true)}
                        data-testid="button-show-overtime"
                        className="btn btn-xs btn-info hidden-sm hidden-xs"
                        title={SystemText('Reviews/Appraisals/Wizard/Buttons/ShowOverTime/Title')}
                        disabled={!competency?.level && !integrationsList.length}
                    >
                        <i className="fa fa-line-chart"></i>
                    </button>
                    {hasModule(user, 'integrations') && integrationsList.length > 0 && (
                        <span className="hidden-sm hidden-xs">
                            <img
                                title={withText}
                                className="crm-icon left-15"
                                src="/dist/img/metrics-icon-ric.png"
                                alt=""
                                loading="lazy"
                            />
                        </span>
                    )}
                </span>
                {hasModule(user, 'objectives') && permissions.objectives.canView && (
                    <span className="btn-badge hidden-sm hidden-xs">
                        <button
                            onClick={() => setShowTasks(true)}
                            data-testid="button-show-tasks"
                            className={`btn-badge btn btn-xs hidden-sm hidden-xs btn-${previousTasks ? 'info' : 'default'}`}
                            title={SystemText('Reviews/Appraisals/Wizard/Buttons/ViewObjectives/Title')}
                            disabled={previousTasks === 0}
                        >
                            <i className="fa fa-tasks"></i>
                        </button>
                        {pastTasks > 0 && <span className="badge label mlm10 label-danger">{pastTasks}</span>}
                    </span>
                )}
            </div>
        );
    };

    const NextPrevButtons = () => {
        let nextStub = 'Next';
        let pageLength = pages.length;

        if (startPage > 0 && endPage + 1 - startPage === page) {
            nextStub = state.readonly ? 'BackToSummary' : 'Update';
        }

        if (startPage > 0) pageLength = endPage - startPage + 1;

        return (
            <div className="form">
                <div className="flex flex-center">
                    <div style={{ minWidth: '150px' }}>
                        {!inReview && !state.readonly && !startPage && (
                            <button
                                id="prev"
                                className="btn btn-success"
                                data-testid="button-appraisal-prev"
                                disabled={page < 0 || sliding}
                                onClick={prev}
                            >
                                {SystemText('Reviews/Appraisals/Wizard/Buttons/Back')}
                            </button>
                        )}
                    </div>
                    <div className="flex-grow text-center">
                        {((!inReview && page > 0 && page <= pageLength) || (startPage > 0 && startPage < endPage)) && (
                            <span>
                                {page} / {pageLength}
                            </span>
                        )}
                    </div>
                    <div style={{ minWidth: '150px' }} className="text-right">
                        {page <= pages.length && (
                            <button
                                id="next"
                                className="btn btn-success"
                                disabled={(page + 1 > pages.length && inPreview) || sliding}
                                data-testid="button-appraisal-next"
                                onClick={next}
                            >
                                {SystemText(`Reviews/Appraisals/Wizard/Buttons/${nextStub}`)}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const footer =
        congrats || inPreview ? null : competency || state.readonly ? (
            <NextPrevButtons key="footer" />
        ) : page < 1 ? null : page > pages.length ? (
            <button key="footer-submit" className="btn btn-success" onClick={next} data-testid="button-appraisal-submit">
                {SystemText('Reviews/Appraisals/Wizard/Buttons/SubmitRatings')}
            </button>
        ) : null;

    const marks = [
        { value: 0, label: '0' },
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
    ].filter(m => m.value <= ratingRange);

    const reviewButton = () => (
        <div className="mt20">
            <button
                className="btn btn-primary"
                onClick={() => {
                    setCongrats(false);
                    setStage(pages.length + 1);
                }}
            >
                <Copy value={SystemText('Reviews/Appraisals/Wizard/Buttons/ReviewNow')} />
            </button>
        </div>
    );

    const rescheduleButton = () => (
        <div className="mt20">
            <button className="btn btn-primary" disabled={!unassignReschedule} onClick={() => unassignReschedule(appraisal)}>
                <Copy value={SystemText('Reviews/Appraisals/Wizard/Buttons/Reschedule')} />
            </button>
        </div>
    );

    const continueButton = () => {
        if (user.licence !== 'FULL') return null;

        const allowReset = !user.samlOnly && !user.hasPassword;
        const link = state.user
            ? allowReset
                ? `/reset-password?email=${encodeURIComponent(user.email)}&guid=${guid}`
                : '/develop/performance-dashboard'
            : `/develop/reviews/${profile.id}`;
        const text = state.user
            ? allowReset
                ? SystemText('Reviews/Appraisals/Wizard/Congratulations/Password/Button')
                : SystemText('Reviews/Appraisals/Wizard/Congratulations/User/Button', profile.first_Name)
            : SystemText('Reviews/Appraisals/Wizard/Congratulations/Manager/Button', profile.first_Name);

        return (
            <div className="mt10">
                <p>{SystemText('Reviews/Appraisals/Wizard/Congratulations/Body/Or')}</p>
                <button className="btn btn-primary" onClick={() => history.push(link)}>
                    {text}
                </button>
                {state.wizard && (
                    <p className="mt20 text-sm">{SystemText('Reviews/Appraisals/Wizard/Congratulations/Wizard/Login')}</p>
                )}
            </div>
        );
    };

    const who = appraisal.participants === 'UM' ? '/Both' : appraisal.participants === 'U' ? '/User' : '/Manager';

    let how = user.setInReview ? '/1-2-1' : '/Auto';
    let login = state.user ? '/User' : '/Manager';
    let rest =
        (state.manager && appraisal.user_Comments_Done) || (state.user && appraisal.manager_Comments_Done)
            ? '/Complete'
            : '/Incomplete';

    if (who !== '/Both') {
        how = '';
        rest = '';
        login = '';
    }

    const mainContent = (
        <div className="text-left" key="main-content">
            {state.invalidManager ? (
                <div>
                    <div className="uppercase">
                        {SystemText('Reviews/Appraisals/Wizard/Scheduled')} {e4Format(appraisal.scheduled)}
                    </div>
                    <h2>{SystemText('Reviews/Appraisals/Wizard/WrongManager/Header')}</h2>
                    <div>
                        <Copy
                            value={SystemText(
                                `Reviews/Appraisals/Wizard/WrongManager/${
                                    state.appraisalManager
                                        ? 'OldManager'
                                        : state.userManager || user.administrator
                                        ? 'NewManager'
                                        : 'User'
                                }`
                            )}
                        />
                        {(state.userManager || user.administrator) && rescheduleButton()}
                    </div>
                </div>
            ) : state.invalidCoach ? (
                <div>
                    <div className="uppercase">
                        {SystemText('Reviews/Appraisals/Wizard/Scheduled')} {e4Format(appraisal.scheduled)}
                    </div>
                    <h2>{SystemText('Reviews/Appraisals/Wizard/WrongCoach/Header')}</h2>
                    <div>
                        <Copy
                            value={SystemText(
                                `Reviews/Appraisals/Wizard/WrongCoach/${
                                    state.appraisalCoach
                                        ? 'OldCoach'
                                        : state.userCoach || user.administrator
                                        ? 'NewCoach'
                                        : 'User'
                                }`
                            )}
                        />
                        {(state.userCoach || user.administrator) && rescheduleButton()}
                    </div>
                </div>
            ) : congrats && !state.past ? (
                <div>
                    <div className="uppercase">
                        {SystemText('Reviews/Appraisals/Wizard/Scheduled')} {e4Format(appraisal.scheduled)}
                    </div>
                    <h2>{SystemText('Reviews/Appraisals/Wizard/Congratulations/Header')}</h2>
                    <div>
                        <Copy
                            value={SystemText(
                                `Reviews/Appraisals/Wizard/Congratulations/Body${who}${how}${login}${rest}`,
                                profile.first_Name
                            )}
                        />
                        {reviewButton()}
                        {continueButton()}
                    </div>
                </div>
            ) : competency ? (
                <Rating
                    pages={pages.filter((p, i) => startPage === 0 || (i + 1 >= startPage && i + 1 <= endPage))}
                    page={page}
                    setPage={setPage}
                    showPredictions={false}
                    showBothManagerAndStaffRatingInfo={showBothManagerAndStaffRatingInfo}
                    profile={profile}
                    ratingRange={ratingRange}
                    usePIs={usePIs}
                    state={state}
                    competency={competency}
                    rate={rate}
                    predictions={predictions}
                    marks={marks}
                    error={error}
                    buttons={buttons}
                    comment={comment}
                    next={next}
                    sliding={sliding}
                    setSliding={setSliding}
                    prev={prev}
                    goals={goals}
                    inPreview={inPreview}
                    inReview={inReview}
                    appraisal={appraisal}
                />
            ) : page < 1 ? (
                <Intro
                    appraisal={appraisal}
                    profile={profile}
                    setPage={setPage}
                    state={state}
                    setGoals={setGoals}
                    goals={goals}
                    rate={rate}
                    next={next}
                    inReview={inReview}
                    setInReview={setInReview}
                    pages={pages}
                    setPredictions={setPredictions}
                    predictions={predictions}
                    inPreview={inPreview}
                />
            ) : page > pages.length ? (
                inPreview ? (
                    <div>{SystemText('Reviews/Appraisals/Wizard/PreviewSummary')}</div>
                ) : (
                    <Summary
                        key={`review-summary${appraisal.id}`}
                        appraisal={appraisal}
                        profile={profile}
                        predictions={predictions}
                        pages={pages}
                        finalise={finalise}
                        state={state}
                        comments={comments}
                        setComments={setComments}
                        setAgreedRatingValue={setAgreedRatingValue}
                        error={error}
                        setInReview={setInReview}
                        setPage={setPage}
                        setStartPage={setStartPage}
                        setEndPage={setEndPage}
                        usePIs={usePIs}
                        goals={goals}
                    />
                )
            ) : (
                <div>{SystemText('Reviews/Appraisals/Wizard/NoCompetencies')}</div>
            )}
        </div>
    );

    return (
        <div className="assessment-wizard" id="assessment-wizard">
            {[mainContent, footer]}

            <CompetencyTasks
                summary
                profile={profile}
                competency={competency}
                show={showTasks}
                close={() => setShowTasks(false)}
            />
            <CompetencyOverTime
                profile={profile}
                competency={competency}
                show={showOverTime}
                close={() => setShowOverTime(false)}
            />
            {showRestOfTeam && (
                <CompetencyGridUsers
                    loadFromTeam
                    competency={competency}
                    show={showRestOfTeam}
                    close={() => {
                        setShowRestOfTeam(false);
                    }}
                />
            )}
        </div>
    );
};

export default withRouter(Wizard);
